import { forwardRef } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames/bind';

import styles from './Input.module.scss';

const cx = classNames.bind(styles);

const mapDataAttributes = dataAttributes =>
  Object.keys(dataAttributes).reduce((acc, key) => {
    acc[`data-${key}`] = dataAttributes[key];
    return acc;
  }, {});

const noop = () => {};
const EMPTY_OBJECT = {};

const BaseInput = ({
  inputRef = null,
  autoComplete = false,
  checked = false,
  className = '',
  dataAttributes = EMPTY_OBJECT,
  disabled = false,
  id = undefined,
  onChange = noop,
  onFocus = noop,
  onKeyDown = noop,
  onClick = noop,
  pattern = undefined,
  placeholder = undefined,
  readOnly = false,
  required = false,
  type = 'text',
  value = undefined,
  dataTest = '',
  ...inputProps
}) => {
  const extendedProps = {
    className,
    disabled,
    id,
    onChange,
    onFocus,
    onKeyDown,
    onClick,
    placeholder,
    readOnly,
    required,
    value,
    ...inputProps
  };
  if (type === 'checkbox') {
    extendedProps.checked = checked;
  } else if (type === 'email' || type === 'password') {
    extendedProps.pattern = pattern;
  }

  const mapped = mapDataAttributes(dataAttributes);

  return (
    <input
      {...extendedProps}
      {...mapped}
      autoComplete={autoComplete ? autoComplete.toString() : undefined}
      ref={inputRef}
      type={type}
      data-test={dataAttributes.test || dataTest}
    />
  );
};

BaseInput.propTypes = {
  autoComplete: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  checked: PropTypes.bool,
  className: PropTypes.string,
  dataAttributes: PropTypes.object,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.string,
  dataTest: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ])
};

const Input = forwardRef((props, ref) => (
  <BaseInput {...props} inputRef={ref} />
));

Input.propTypes = BaseInput.propTypes;

const StyledInput = ({
  size = 'small',
  width = 'full',
  className = '',
  type = 'text',
  disabled,
  ...props
}) => {
  const cn = cx(
    'input',
    {
      disabled
    },
    styles[type],
    styles[size],
    styles[width],
    className
  );

  return (
    <Input
      {...props}
      className={cn}
      type={type}
      disabled={disabled}
      width={width}
    />
  );
};

StyledInput.propTypes = {
  ...Input.propTypes,
  size: PropTypes.oneOf(['normal', 'small', 'big']),
  width: PropTypes.oneOf(['full'])
};

export { Input };
export default StyledInput;
